import QRCode from "easyqrcodejs"

export type AdvertShareModalData = {
    cityName: string
    socialMediaLink: string
    thumbnailSrc: string
    title: string
    clubSlogan: string | null
}

type ComponentMap = {
    cityName: HTMLElement
    clubSlogan: HTMLElement
    directLink: HTMLInputElement
    shareByMail: HTMLAnchorElement
    shareQR: HTMLElement
    shareWithWhatsapp: HTMLAnchorElement
    shareWithFacebook: HTMLAnchorElement
    shareWithX: HTMLAnchorElement
    thumbnail: HTMLImageElement
    title: HTMLElement
}

type ComponentKey = keyof ComponentMap

const ModalComponents: { [K in keyof ComponentMap]: K } = {
    cityName: "cityName",
    clubSlogan: "clubSlogan",
    directLink: "directLink",
    shareByMail: "shareByMail",
    shareQR: "shareQR",
    shareWithWhatsapp: "shareWithWhatsapp",
    shareWithFacebook: "shareWithFacebook",
    shareWithX: "shareWithX",
    thumbnail: "thumbnail",
    title: "title",
}

namespace Modal {
    export class AdvertShareModal {
        private readonly modalContainerElement: HTMLElement
        private readonly components: ComponentMap

        /** @throws Error */
        constructor() {
            const container: HTMLElement | null = document.getElementById("merkzettel-modal-share")

            if (!container) {
                throw new Error("AdvertShareModal container not found")
            }

            this.modalContainerElement = container
            this.components = this.collectComponents()
        }

        public modal<T = HTMLElement>(wrapperCallback?: (element: HTMLElement) => T): T {
            if (!wrapperCallback) {
                return this.modalContainerElement as T
            }

            return wrapperCallback(this.modalContainerElement)
        }

        public updateModalData(data: AdvertShareModalData): void {
            this.components.cityName.textContent = data.cityName

            const thumbnail: HTMLImageElement = this.components.thumbnail
            thumbnail.src = data.thumbnailSrc
            thumbnail.alt = data.title

            this.components.title.textContent = data.title
            this.components.clubSlogan.textContent = data.clubSlogan ?? ""

            this.components.directLink.value = data.socialMediaLink
            this.generateQRCodeImage(data.socialMediaLink)

            const advertName = encodeURIComponent(data.title)
            const advertLink = encodeURIComponent(data.socialMediaLink)

            this.components.shareByMail.href = this.generateMailLink(advertName, advertLink)
            this.components.shareWithFacebook.href = this.generateFacebookLink(advertLink)
            this.components.shareWithWhatsapp.href = this.generateWhatsappLink(advertLink)
            this.components.shareWithX.href = this.generateXLink(advertLink)
        }

        /** @throws Error */
        private collectComponents(): ComponentMap {
            const elements: NodeListOf<HTMLElement> =
                this.modalContainerElement.querySelectorAll<HTMLElement>("[data-component]")

            const map = {} as Partial<ComponentMap>

            for (const element of elements) {
                const name = element.dataset.component as ComponentKey | undefined

                if (!name) {
                    continue
                }

                switch (name) {
                    case ModalComponents.cityName:
                    case ModalComponents.clubSlogan:
                    case ModalComponents.shareQR:
                    case ModalComponents.title:
                        map[name] = element
                        break

                    case ModalComponents.shareByMail:
                    case ModalComponents.shareWithWhatsapp:
                    case ModalComponents.shareWithFacebook:
                    case ModalComponents.shareWithX:
                        map[name] = element as HTMLAnchorElement
                        break

                    case ModalComponents.directLink:
                        map.directLink = element as HTMLInputElement
                        break

                    case ModalComponents.thumbnail:
                        map.thumbnail = element as HTMLImageElement
                        break

                    default:
                        console.warn(`Unknown component: ${name}`)
                }
            }

            this.ensureRequiredComponentsExist(map)

            return map as ComponentMap
        }

        /** @throws Error */
        private ensureRequiredComponentsExist(map: Partial<ComponentMap>): void {
            const requiredKeys: ComponentKey[] = Object.values(ModalComponents)

            for (const key of requiredKeys) {
                if (!map[key]) {
                    throw new Error(`Missing required modal component: ${key}`)
                }
            }
        }

        private generateMailLink(advertName: string, advertLink: string): string {
            return `mailto:?subject=${advertName} auf Ladies.de&body=Hallo,%0D%0A%0D%0Aunter folgendem Link findest du die Anzeige von ${advertName}:%0D%0A${advertLink}`
        }

        private generateFacebookLink(advertLink: string): string {
            return `https://www.facebook.com/share.php?u=${advertLink}`
        }

        private generateWhatsappLink(advertLink: string): string {
            return `whatsapp://send?text=${advertLink}`
        }

        private generateXLink(advertLink: string): string {
            return `https://x.com/intent/post?url=${advertLink}`
        }

        private generateQRCodeImage(advertLink: string): void {
            const config = {
                text: `${advertLink}`,
            }

            this.components.shareQR.textContent = ""
            new QRCode(this.components.shareQR, config)
        }
    }
}

export const AdvertShareModal = new Modal.AdvertShareModal()
